// Deterministic synthetic genome for demoing the UI with zero real data.
// Uses a small seeded PRNG so the "sample" looks the same every load and is
// obviously not anyone's actual genome.

import { CHROM_ORDER, type ParsedGenome, type Variant } from '../types';
import { BUILD_CHROM_LENGTHS } from './cytobands';

// The sample declares itself as a 23andMe export, and those are GRCh37 — so it
// must draw positions from GRCh37 lengths. Using the GRCh38 table in types.ts
// produced positions past the end of several chromosomes, which showed up as
// markers floating off the ends of the ideogram.
//
// MT is added back by hand: the cytoband table has no mitochondrial entry
// (a 16.6 kb circle has no stained bands), but CHROM_ORDER includes MT, and a
// zero length here makes the position-dedupe loop below spin forever.
const CHROM_LENGTHS: Record<string, number> = {
  ...BUILD_CHROM_LENGTHS.hg19,
  MT: 16569,
};
import { CONDITIONS } from './conditions';
import { effectFreq, SUPERPOPS, SUPERPOP_LABELS, type Superpop } from './frequencies';

/** Mulberry32 — tiny deterministic PRNG. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const BASES = ['A', 'C', 'G', 'T'] as const;

/** Weighted so most calls are homozygous, some het, a few no-calls. */
function makeGenotype(rand: () => number): string {
  const r = rand();
  const a = BASES[Math.floor(rand() * 4)];
  if (r < 0.03) return '--'; // no-call
  if (r < 0.33) {
    // heterozygous: a different second base
    let b = BASES[Math.floor(rand() * 4)];
    if (b === a) b = BASES[(BASES.indexOf(a) + 1) % 4];
    return `${a}${b}`;
  }
  return `${a}${a}`; // homozygous
}

/**
 * Give the demo genome a real call at every rsID the condition catalog knows
 * about, so the Conditions tab has something to show without real data.
 *
 * Genotypes are drawn under Hardy-Weinberg from the REAL 1000 Genomes
 * frequency of each effect allele in the chosen superpopulation. That makes the
 * demo teach something true rather than merely look busy: an East Asian sample
 * carries ALDH2*2 and essentially never carries the PTPN22 autoimmune allele,
 * a European sample is the other way round, and sickle trait shows up only in
 * the African sample. Those contrasts are the single most important thing this
 * app has to say about ancestry and they were invisible with random genotypes.
 *
 * Still synthetic. Allele draws are independent, so there is no linkage
 * disequilibrium and no real haplotype structure — this is nobody's genome.
 */
function catalogVariants(rand: () => number, pop: Superpop): Variant[] {
  const out: Variant[] = [];
  const seen = new Set<string>();

  for (const condition of CONDITIONS) {
    for (const marker of condition.markers) {
      if (seen.has(marker.rsid)) continue;
      seen.add(marker.rsid);

      const [a, b] = marker.alleles;
      const other = marker.effectAllele === a ? b : a;

      // p = frequency of the effect allele in this population. Fall back to a
      // neutral 0.3 only where 1000G has no entry, rather than inventing one.
      const p = effectFreq(marker.rsid, pop) ?? 0.3;
      const q = 1 - p;

      let genotype: string;
      if (rand() < 0.03) {
        genotype = '--'; // keep the "no call" UI path reachable in the demo
      } else {
        // Hardy-Weinberg: p^2 effect/effect, 2pq het, q^2 other/other.
        const r = rand();
        if (r < p * p) genotype = `${marker.effectAllele}${marker.effectAllele}`;
        else if (r < p * p + 2 * p * q) genotype = `${marker.effectAllele}${other}`;
        else genotype = `${other}${other}`;
      }

      out.push({
        rsid: marker.rsid,
        // Placement is arbitrary — the demo only needs the rsID to resolve.
        chrom: CHROM_ORDER[Math.floor(rand() * 22)],
        pos: 1 + Math.floor(rand() * 200000000),
        genotype,
      });
    }
  }
  return out;
}

export function generateSampleGenome(
  pop: Superpop = 'EUR',
  targetCount = 8000,
  /**
   * Override the PRNG seed. The default is fixed per population so the demo is
   * identical across reloads; passing a seed lets tests draw many independent
   * samples and check that genotypes really follow the population frequencies.
   */
  seed = 20260805 + SUPERPOPS.indexOf(pop) * 7919,
): ParsedGenome {
  const rand = mulberry32(seed);
  const variants: Variant[] = [...catalogVariants(rand, pop)];

  // Distribute variants across chromosomes proportional to length.
  const totalLen = CHROM_ORDER.reduce((s, c) => s + (CHROM_LENGTHS[c] ?? 0), 0);
  let rsCounter = 1000000;

  for (const chrom of CHROM_ORDER) {
    const len = CHROM_LENGTHS[chrom] ?? 0;
    if (len <= 0) continue;
    // Never ask for more distinct positions than the chromosome has.
    const n = Math.min(len, Math.max(4, Math.round((len / totalLen) * targetCount)));
    const positions = new Set<number>();
    for (let i = 0; i < n; i++) {
      let pos = 1 + Math.floor(rand() * len);
      // Avoid exact duplicates, but bounded: an unbounded retry loop here spins
      // forever whenever the requested count approaches the chromosome length.
      for (let tries = 0; positions.has(pos) && tries < 40; tries++) {
        pos = 1 + Math.floor(rand() * len);
      }
      if (positions.has(pos)) continue;
      positions.add(pos);
      variants.push({
        rsid: `rs${rsCounter++}`,
        chrom,
        pos,
        genotype: makeGenotype(rand),
      });
    }
  }

  // Sort by chromosome order then position for a realistic browse experience.
  const order = new Map(CHROM_ORDER.map((c, i) => [c, i]));
  variants.sort((a, b) => {
    const ca = order.get(a.chrom) ?? 99;
    const cb = order.get(b.chrom) ?? 99;
    return ca !== cb ? ca - cb : a.pos - b.pos;
  });

  return {
    format: '23andme',
    variants,
    meta: {
      fileName: `sample-genome — ${SUPERPOP_LABELS[pop]} (synthetic)`,
      lineCount: variants.length,
      truncated: false,
      parsedAt: 0,
    },
    warnings: [],
  };
}
